import { trackProductEvent } from '../lib/product-analytics';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from '../lib/query-keys';
import {
  createPublicShortlistReviewSession,
  createShortlist,
  deleteShortlist,
  fetchPublicShortlist,
  fetchPublicShortlistReviewCandidates,
  fetchPublicShortlistReviewSession,
  fetchShortlistCandidates,
  fetchShortlistDetail,
  fetchShortlistFeedback,
  fetchShortlistFeedbackSummary,
  fetchShortlists,
  removeShortlistCandidate,
  setShortlistCandidates,
  submitPublicShortlistReview,
  updatePublicShortlistReviewDecision,
  updateShortlist,
  type FetchShortlistsParams,
} from '../lib/shortlists-api';
import { useWorkspace } from '../providers/useWorkspace';

function serializeShortlistsParams(params: FetchShortlistsParams = {}) {
  return JSON.stringify({
    q: params.q ?? '',
    cursor: params.cursor ?? null,
    limit: params.limit ?? 50,
  });
}

function invalidateShortlists(
  queryClient: ReturnType<typeof useQueryClient>,
  companyId?: string | null,
) {
  // Use the prefix key so every paginated/searched variant of the list is
  // invalidated. QUERY_KEYS.SHORTLISTS appends a serialized params segment,
  // so the prefix returned by SHORTLISTS_BY_COMPANY matches all of them.
  queryClient.invalidateQueries({
    queryKey: QUERY_KEYS.SHORTLISTS_BY_COMPANY(companyId),
  });
}

export function useShortlistsList(params: FetchShortlistsParams = {}, enabled = true) {
  const { companyId } = useWorkspace();

  return useQuery({
    queryKey: QUERY_KEYS.SHORTLISTS(companyId, serializeShortlistsParams(params)),
    queryFn: () => fetchShortlists(params),
    enabled: Boolean(companyId) && enabled,
    staleTime: 15_000,
  });
}

export function useShortlistDetail(shortlistId: string, enabled = true) {
  return useQuery({
    queryKey: QUERY_KEYS.SHORTLIST_DETAIL(shortlistId),
    queryFn: () => fetchShortlistDetail(shortlistId),
    enabled: enabled && Boolean(shortlistId),
    staleTime: 15_000,
  });
}

export function useShortlistCandidates(shortlistId: string, enabled = true) {
  return useQuery({
    queryKey: QUERY_KEYS.SHORTLIST_CANDIDATES(shortlistId),
    queryFn: () => fetchShortlistCandidates(shortlistId),
    enabled: enabled && Boolean(shortlistId),
    staleTime: 15_000,
  });
}

export function useShortlistFeedback(shortlistId: string, enabled = true) {
  return useQuery({
    queryKey: QUERY_KEYS.SHORTLIST_FEEDBACK(shortlistId),
    queryFn: () => fetchShortlistFeedback(shortlistId),
    enabled: enabled && Boolean(shortlistId),
    staleTime: 15_000,
  });
}

export function useShortlistFeedbackSummary(shortlistId: string, enabled = true) {
  return useQuery({
    queryKey: QUERY_KEYS.SHORTLIST_FEEDBACK_SUMMARY(shortlistId),
    queryFn: () => fetchShortlistFeedbackSummary(shortlistId),
    enabled: enabled && Boolean(shortlistId),
    staleTime: 15_000,
  });
}

export function useCreateShortlist() {
  const queryClient = useQueryClient();
  const { companyId } = useWorkspace();

  return useMutation({
    mutationFn: createShortlist,
    onSuccess: (shortlist) => {
      invalidateShortlists(queryClient, companyId);
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.SHORTLIST_DETAIL(shortlist.id),
      });
    },
  });
}

export function useUpdateShortlist() {
  const queryClient = useQueryClient();
  const { companyId } = useWorkspace();

  return useMutation({
    mutationFn: ({
      shortlistId,
      input,
    }: {
      shortlistId: string;
      input: {
        name?: string;
        description?: string;
        jobDescription?: string;
      };
    }) => updateShortlist(shortlistId, input),
    onSuccess: (shortlist) => {
      invalidateShortlists(queryClient, companyId);
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.SHORTLIST_DETAIL(shortlist.id),
      });
    },
  });
}

export function useDeleteShortlist() {
  const queryClient = useQueryClient();
  const { companyId } = useWorkspace();

  return useMutation({
    mutationFn: deleteShortlist,
    onSuccess: () => invalidateShortlists(queryClient, companyId),
  });
}

export function useSetShortlistCandidates() {
  const queryClient = useQueryClient();
  const { companyId } = useWorkspace();

  return useMutation({
    mutationFn: ({
      shortlistId,
      candidateIds,
    }: {
      shortlistId: string;
      candidateIds: string[];
    }) => setShortlistCandidates(shortlistId, candidateIds),
    onSuccess: (shortlist) => {
      invalidateShortlists(queryClient, companyId);
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.SHORTLIST_DETAIL(shortlist.id),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.SHORTLIST_CANDIDATES(shortlist.id),
      });
    },
  });
}

export function useRemoveShortlistCandidate() {
  const queryClient = useQueryClient();
  const { companyId } = useWorkspace();

  return useMutation({
    mutationFn: ({
      shortlistId,
      candidateId,
    }: {
      shortlistId: string;
      candidateId: string;
    }) => removeShortlistCandidate(shortlistId, candidateId),
    onSuccess: (_value, variables) => {
      invalidateShortlists(queryClient, companyId);
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.SHORTLIST_DETAIL(variables.shortlistId),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.SHORTLIST_CANDIDATES(variables.shortlistId),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.SHORTLIST_FEEDBACK(variables.shortlistId),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.SHORTLIST_FEEDBACK_SUMMARY(variables.shortlistId),
      });
    },
  });
}

export function usePublicShortlist(slug: string, enabled = true) {
  return useQuery({
    queryKey: QUERY_KEYS.PUBLIC_SHORTLIST(slug),
    queryFn: () => fetchPublicShortlist(slug),
    enabled: enabled && Boolean(slug),
    staleTime: 15_000,
  });
}

export function usePublicShortlistReviewCandidates(slug: string, enabled = true) {
  return useQuery({
    queryKey: QUERY_KEYS.PUBLIC_SHORTLIST_REVIEW_CANDIDATES(slug),
    queryFn: () => fetchPublicShortlistReviewCandidates(slug),
    enabled: enabled && Boolean(slug),
    staleTime: 15_000,
  });
}

export function usePublicShortlistReviewSession(
  slug: string,
  sessionId: string,
  sessionToken: string,
  enabled = true,
) {
  return useQuery({
    queryKey: QUERY_KEYS.PUBLIC_SHORTLIST_SESSION(slug, sessionId),
    queryFn: () => fetchPublicShortlistReviewSession(slug, sessionId, sessionToken),
    enabled: enabled && Boolean(slug) && Boolean(sessionId) && Boolean(sessionToken),
    staleTime: 0,
    refetchOnMount: 'always',
  });
}

export function useCreatePublicShortlistReviewSession() {
  return useMutation({
    mutationFn: ({
      slug,
      reviewerName,
    }: {
      slug: string;
      reviewerName: string;
    }) => createPublicShortlistReviewSession(slug, reviewerName),
    // The B2B virality loop after shortlist_created: a reviewer showed up.
    onSuccess: () => trackProductEvent('shortlist_review_session_started', {}),
  });
}

export function useUpdatePublicShortlistReviewDecision() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      slug,
      sessionId,
      sessionToken,
      candidateId,
      input,
    }: {
      slug: string;
      sessionId: string;
      sessionToken: string;
      candidateId: string;
      input: {
        decision?: 'pending' | 'shortlisted' | 'passed';
        note?: string;
      };
    }) =>
      updatePublicShortlistReviewDecision(
        slug,
        sessionId,
        sessionToken,
        candidateId,
        input,
      ).then((result) => {
        trackProductEvent('shortlist_review_decision_made', {
          decision: input.decision ?? 'pending',
          has_note: Boolean((input as { note?: string | null }).note),
        });
        return result;
      }),
    onSuccess: (session) => {
      queryClient.setQueryData(
        QUERY_KEYS.PUBLIC_SHORTLIST_SESSION(session.slug, session.id),
        session,
      );
    },
  });
}

export function useSubmitPublicShortlistReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      slug,
      sessionId,
      sessionToken,
    }: {
      slug: string;
      sessionId: string;
      sessionToken: string;
    }) => submitPublicShortlistReview(slug, sessionId, sessionToken),
    onSuccess: (result) => {
      trackProductEvent('shortlist_review_submitted', {});
      queryClient.setQueryData(
        QUERY_KEYS.PUBLIC_SHORTLIST_SESSION(result.session.slug, result.session.id),
        result.session,
      );
    },
  });
}
